// purpose: 本編 R2 経路の体感層。sim を読むだけで静止カメラの衝撃収束・撃破インパクト・射撃フィール・常時速度層・排気トレイルを出す。

import { Particle, ParticleContainer, Rectangle, Texture, type Container, type Graphics } from 'pixi.js';
import { EnemyState, EnemyType, INTERNAL_HEIGHT, INTERNAL_WIDTH, Owner, POOL_CAPACITY } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { LayerStackAsset } from '../../layerstack/LayerStackLoader';
import type { RendererSettings } from '../../port';
import type { MobilePoolObserver, MobilePoolSnapshot } from '../MobilePoolObserver';
import { R2_HEIGHT, R2_WIDTH } from '../R2Renderer';
import { WORLD_TO_R2 } from '../scale';
import { FX_SPARK_CORE, FX_SPARK_SMALL } from './FxDirector';
import type { MobileImpactPrimitives } from './MobileImpactPrimitives';
import { OneShotFx } from './OneShotFx';
import { PixelBurst } from './PixelBurst';
import { RING_TONE_DEATH, RING_TONE_IMPACT, ShockRings } from './ShockRings';

/**
 * Game feel for the real loop, observed rather than driven: the simulation is
 * read-only here, so every beat below is a prev-state edge (screenShakeTicks
 * rising, an enemy entering Dying, a projectile slot flipping) rather than a
 * call the sim makes. That is what lets the sim stay untouched while the
 * renderer gains impact.
 *
 * Two coordinate facts bound this file: sim values are 384x216 and become R2
 * pixels only through WORLD_TO_R2, and the speed layer is a *visual* scroll
 * offset — it moves the parallax bands, never an entity, so nothing the player
 * aims at is displaced by it.
 *
 * The speed layer is unconditional. An earlier revision gated the boost and the
 * streaks behind combo tier >= 1, which made normal play byte-for-byte
 * identical to no feel layer at all; tier 0 now carries its own boost and its
 * own streak density, and the tiers stack on top of that floor.
 */

/** Impact sink, structural: this file must not know R2Renderer's concrete type. */
export interface GameFeelHost {
  setImpactZoom(scale: number): void;
  /** Normalised 0..1 impact envelope for the full-frame chromatic post pass. */
  setImpactPulse(level: number): void;
}

export interface GameFeelDeps {
  readonly host: GameFeelHost;
  /** Shared fixed-capacity pool observation; null on the unchanged desktop path. */
  readonly mobilePools: MobilePoolObserver | null;
  /** OneShotFx sprites and PixelBurst pixels; both above the entities, below the flash overlay. */
  readonly sparkLayer: Container;
  readonly burstLayer: Container;
  /** Cleared and redrawn here every frame. `speedLines` sits behind entities, `exhaust` behind the ship, `muzzle` and `rings` in front. */
  readonly speedLines: Graphics;
  readonly exhaust: Graphics;
  readonly muzzle: Graphics;
  readonly rings: Graphics | null;
  readonly mobileImpact: MobileImpactPrimitives | null;
  readonly fxSlugs: readonly string[];
  readonly fxAssets: readonly LayerStackAsset[];
}

/**
 * Combo tier thresholds. Source of truth: src/game/runtime/comboPhase.ts
 * (RUSH 5 / STORM 10 / OVERDRIVE 20). Duplicated as consts because render must
 * not import runtime; if the table there moves, move it here too.
 */
const COMBO_TIER_CHAINS: readonly number[] = Object.freeze([0, 5, 10, 20]);
/**
 * Visual scroll multiplier per tier. Tier 0 is well above 1 on purpose: the
 * background has to read as fast from the first second of a run, before any
 * chain exists. Combo then buys the difference between fast and violent.
 */
const SCROLL_BOOST_BY_TIER: readonly number[] = Object.freeze([1.35, 1.6, 1.85, 2.2]);
const SPEED_LINES_BY_TIER: readonly number[] = Object.freeze([4, 8, 14, 22]);
/** Exponential approach, framerate-independent: 63% of the way there in this many seconds. */
const BOOST_TIME_CONSTANT_S = 0.4;
const BASE_SCROLL_BOOST = SCROLL_BOOST_BY_TIER[0]!;

/**
 * R2本編はカメラ位置も全景スケールも動かさない。旧screenShakeTicksはイベントの
 * 強度信号としてだけ読み、四辺の収束線・局所リング・色収差へ変換する。
 */
const IMPACT_PULSE_COMBO = 0.58;
const IMPACT_PULSE_LARGE = 0.76;
const IMPACT_PULSE_DEATH = 1;
const IMPACT_PULSE_TIME_CONSTANT_MS = 96;
const IMPACT_PULSE_EPSILON = 0.003;
const IMPACT_COMBO_TICKS = 12;
const IMPACT_DEATH_TICKS = 18;

/** The sim's own big/small split (GameWorld.destroyEnemy); kept identical so the local impact grade matches gameplay. */
const BIG_ENEMY_MIN_TYPE = EnemyType.ChoirEngine;

/** Per-frame spawn budgets: a wave wiping out at once must not become a wall of debris. */
const MUZZLE_PER_FRAME_CAP = 1;
const IMPACT_BURSTS_PER_FRAME_CAP = 4;

/**
 * Muzzle flash. Two discrete grades over 70ms — bright and fat on the first
 * frames, dim and small on the last — so a held trigger sparks visibly at the
 * barrel instead of only implying recoil.
 */
const MUZZLE_SLOTS = 8;
const MUZZLE_LIFE_MS = 70;
const MUZZLE_GRADES: readonly ParticleGrade[] = Object.freeze([
  { style: { color: 0x83f5ff, alpha: 0.6 }, size: 4 },
  { style: { color: 0x5cd0f1, alpha: 0.3 }, size: 3 },
]);
const MUZZLE_OFFSETS_X: readonly number[] = Object.freeze([0, 3, 5]);
const MUZZLE_OFFSETS_Y: readonly number[] = Object.freeze([0, -2, 2]);

/**
 * Speed lines. Discrete grades rather than a ramp, matching PixelBurst: a
 * smooth fade reads as a blur against native pixel art. Each grade fixes alpha,
 * colour and height together, which is what lets the whole system draw in three
 * `fill()` calls per frame and what mixes 1px and 2px streaks without a second
 * per-slot field.
 */
const SPEED_LINE_SLOTS = 28;
const SPEED_LINE_SPAWNS_PER_FRAME = 3;
const SPEED_LINE_MIN_LENGTH_PX = 32;
const SPEED_LINE_MAX_LENGTH_PX = 96;
const SPEED_LINE_MIN_SPEED_PX_PER_SECOND = 900;
const SPEED_LINE_MAX_SPEED_PX_PER_SECOND = 1400;
const SPEED_LINE_SPAWN_MARGIN_PX = 120;
/** Colours cited from fx/PixelBurst.ts SPARK_COLORS (shipped `small-hit-spark` manifest, ramps.optical upper end). */
const SPEED_LINE_GRADES: readonly StreakGrade[] = Object.freeze([
  { style: { color: 0x83f5ff, alpha: 0.7 }, height: 2 },
  { style: { color: 0x5cd0f1, alpha: 0.45 }, height: 1 },
  { style: { color: 0x5cd0f1, alpha: 0.22 }, height: 1 },
]);

/**
 * Engine exhaust. A continuous pixel stream off the ship's tail, alive whenever
 * the ship is: it is the one speed cue anchored to the player rather than to
 * the background, so it keeps reading even when the parallax is empty. Combo
 * thickens the stream rather than lengthening it, which keeps the ship's
 * silhouette legible at OVERDRIVE.
 */
const EXHAUST_SLOTS = 48;
const EXHAUST_SPAWNS_PER_SECOND = 26;
const EXHAUST_SPAWN_RATE_PER_TIER = 0.5;
const EXHAUST_SPAWNS_PER_FRAME_CAP = 3;
const EXHAUST_REAR_OFFSET_PX = 14;
const EXHAUST_JITTER_Y_PX = 1;
const EXHAUST_LIFE_MIN_MS = 200;
const EXHAUST_LIFE_MAX_MS = 300;
const EXHAUST_DRIFT_MIN_PX_PER_SECOND = 220;
const EXHAUST_DRIFT_MAX_PX_PER_SECOND = 320;
/** Exhaust particles are square, unlike the streaks: `size` is both the width and the height. */
const EXHAUST_GRADES: readonly ParticleGrade[] = Object.freeze([
  { style: { color: 0x83f5ff, alpha: 0.65 }, size: 2 },
  { style: { color: 0x5cd0f1, alpha: 0.4 }, size: 2 },
  { style: { color: 0x5cd0f1, alpha: 0.2 }, size: 1 },
]);

const MS_PER_SECOND = 1000;
const WHITE_WIDTH = Math.max(1, Texture.WHITE.width);
const WHITE_HEIGHT = Math.max(1, Texture.WHITE.height);

interface FillStyle {
  readonly color: number;
  readonly alpha: number;
}
interface StreakGrade {
  readonly style: FillStyle;
  readonly height: number;
}
/** A square mark: one `size` for both axes. Muzzle pixels and exhaust particles share the shape. */
interface ParticleGrade {
  readonly style: FillStyle;
  readonly size: number;
}

export class GameFeelDirector {
  private host: GameFeelHost | null = null;
  private mobilePools: MobilePoolObserver | null = null;
  private speedLines: Graphics | null = null;
  private exhaust: Graphics | null = null;
  private muzzle: Graphics | null = null;
  private rings: Graphics | null = null;
  private mobileImpact: MobileImpactPrimitives | null = null;
  private oneShots: OneShotFx | null = null;
  private bursts: PixelBurst | null = null;
  private readonly shockRings = new ShockRings();
  private readonly speedParticles: Particle[] = [];
  private readonly exhaustParticles: Particle[] = [];
  private readonly muzzleParticles: Particle[] = [];

  private prevScreenShakeTicks = 0;
  private prevScrollX = 0;
  /** Starts at the tier-0 boost, not at 1: a fresh run must not spend half a second ramping into its own baseline. */
  private boost = BASE_SCROLL_BOOST;
  private extraScroll = 0;
  private impactPulse = 0;
  private impactX = R2_WIDTH / 2;
  private impactY = R2_HEIGHT / 2;
  private exhaustCredit = 0;

  private readonly prevEnemyState = new Uint8Array(POOL_CAPACITY.enemies);
  private readonly prevProjectileActive = new Uint8Array(POOL_CAPACITY.projectiles);
  private readonly lastProjectileX = new Float32Array(POOL_CAPACITY.projectiles);
  private readonly lastProjectileY = new Float32Array(POOL_CAPACITY.projectiles);

  private readonly muzzleX = new Float32Array(MUZZLE_SLOTS);
  private readonly muzzleY = new Float32Array(MUZZLE_SLOTS);
  private readonly muzzleAgeMs = new Float32Array(MUZZLE_SLOTS);
  private readonly muzzleGrade = new Uint8Array(MUZZLE_SLOTS);
  private readonly muzzleLive = new Uint8Array(MUZZLE_SLOTS);
  private muzzleCursor = 0;

  private readonly lineX = new Float32Array(SPEED_LINE_SLOTS);
  private readonly lineY = new Float32Array(SPEED_LINE_SLOTS);
  private readonly lineLength = new Float32Array(SPEED_LINE_SLOTS);
  private readonly lineSpeed = new Float32Array(SPEED_LINE_SLOTS);
  private readonly lineGrade = new Uint8Array(SPEED_LINE_SLOTS);
  private readonly lineLive = new Uint8Array(SPEED_LINE_SLOTS);
  private lineCursor = 0;

  private readonly exhaustX = new Float32Array(EXHAUST_SLOTS);
  private readonly exhaustY = new Float32Array(EXHAUST_SLOTS);
  private readonly exhaustDrift = new Float32Array(EXHAUST_SLOTS);
  private readonly exhaustAgeMs = new Float32Array(EXHAUST_SLOTS);
  private readonly exhaustLifeMs = new Float32Array(EXHAUST_SLOTS);
  private readonly exhaustGrade = new Uint8Array(EXHAUST_SLOTS);
  private readonly exhaustLive = new Uint8Array(EXHAUST_SLOTS);
  private exhaustCursor = 0;

  constructor(private readonly minimalComboPresentation = false) {}

  init(deps: GameFeelDeps): void {
    this.host = deps.host;
    this.mobilePools = deps.mobilePools;
    this.speedLines = deps.speedLines;
    this.exhaust = deps.exhaust;
    this.muzzle = deps.muzzle;
    this.rings = deps.rings;
    this.mobileImpact = deps.mobileImpact;
    const oneShots = new OneShotFx(deps.sparkLayer, this.minimalComboPresentation);
    for (let i = 0; i < deps.fxSlugs.length; i += 1) {
      const asset = deps.fxAssets[i];
      if (!asset) continue;
      oneShots.register(deps.fxSlugs[i]!, asset);
    }
    this.oneShots = oneShots;
    this.bursts = new PixelBurst(deps.burstLayer, this.minimalComboPresentation);
    if (this.minimalComboPresentation) {
      this.initMobileParticles(deps.speedLines, deps.exhaust, deps.muzzle);
    }
  }

  /** Visual scroll in simulation units: the sim's own scroll plus what the boost has added. */
  visualScrollX(world: GameWorld): number {
    return world.scrollX + this.extraScroll;
  }

  get activeOneShotCount(): number {
    return (this.oneShots?.liveCount ?? 0) + (this.bursts?.liveCount ?? 0);
  }

  sync(world: GameWorld, deltaMs: number, settings: RendererSettings): void {
    const speedLines = this.speedLines;
    const exhaust = this.exhaust;
    const muzzle = this.muzzle;
    const rings = this.rings;
    const mobileImpact = this.mobileImpact;
    const oneShots = this.oneShots;
    const bursts = this.bursts;
    if (!speedLines || !exhaust || !muzzle || !oneShots || !bursts) return;
    if (this.minimalComboPresentation && !mobileImpact) return;
    if (!this.minimalComboPresentation && !rings) return;
    if (!this.minimalComboPresentation) {
      speedLines.clear();
      exhaust.clear();
      muzzle.clear();
      rings?.clear();
    } else {
      mobileImpact?.beginFrame();
    }
    this.captureImpact(world, settings);
    this.syncKills(world, oneShots, bursts, settings);
    this.syncShots(world, bursts);
    this.syncSpeedLayer(world, deltaMs);
    oneShots.update(deltaMs);
    bursts.update(deltaMs);
    this.drawMuzzle(muzzle, deltaMs);
    this.drawSpeedLines(speedLines);
    this.syncExhaust(world, deltaMs);
    this.drawExhaust(exhaust);
    this.shockRings.update(deltaMs);
    if (this.minimalComboPresentation && mobileImpact) {
      this.shockRings.drawMobile(mobileImpact);
      this.drawMobileImpactCompression(mobileImpact, settings);
    } else if (rings) {
      this.shockRings.draw(rings);
      this.drawImpactCompression(rings, settings);
    }
    this.applyImpactPulse(deltaMs, settings);
  }

  /** A stage start rewinds the sim clock; every prev-state below belongs to the scene that ended. */
  onSceneReset(): void {
    this.prevScreenShakeTicks = 0;
    this.prevScrollX = 0;
    this.boost = BASE_SCROLL_BOOST;
    this.extraScroll = 0;
    this.impactPulse = 0;
    this.impactX = R2_WIDTH / 2;
    this.impactY = R2_HEIGHT / 2;
    this.exhaustCredit = 0;
    this.prevEnemyState.fill(0);
    this.prevProjectileActive.fill(0);
    this.muzzleLive.fill(0);
    this.lineLive.fill(0);
    this.exhaustLive.fill(0);
    this.shockRings.reset();
    this.mobileImpact?.beginFrame();
    this.oneShots?.reset();
    this.bursts?.reset();
    for (const particle of this.speedParticles) particle.alpha = 0;
    for (const particle of this.exhaustParticles) particle.alpha = 0;
    for (const particle of this.muzzleParticles) particle.alpha = 0;
  }

  dispose(): void {
    this.host = null;
    this.mobilePools = null;
    this.speedLines = null;
    this.exhaust = null;
    this.muzzle = null;
    this.rings = null;
    this.mobileImpact = null;
    this.oneShots = null;
    this.bursts = null;
    this.speedParticles.length = 0;
    this.exhaustParticles.length = 0;
    this.muzzleParticles.length = 0;
  }

  /**
   * screenShakeTicks はsim互換のイベント信号として残すが、R2本編では一切カメラへ
   * 渡さない。12以上はコンボ昇格、18以上は死亡／ボス級として全景収束へ変換する。
   * 大型敵の8tickは位置が取れる syncKills() 側で局所演出にする。
   */
  private captureImpact(world: GameWorld, settings: RendererSettings): void {
    const ticks = world.screenShakeTicks;
    const rise = ticks - this.prevScreenShakeTicks;
    this.prevScreenShakeTicks = ticks;
    if (rise <= 0 || settings.shake <= 0) return;
    if (
      this.minimalComboPresentation
      && ticks >= IMPACT_COMBO_TICKS
      && ticks < IMPACT_DEATH_TICKS
    ) return;
    const x = world.player.x * WORLD_TO_R2;
    const y = world.player.y * WORLD_TO_R2;
    if (ticks >= IMPACT_DEATH_TICKS) {
      this.requestImpact(IMPACT_PULSE_DEATH, settings.shake, x, y);
      this.shockRings.spawn(x, y, RING_TONE_DEATH);
    } else if (ticks >= IMPACT_COMBO_TICKS) {
      this.requestImpact(IMPACT_PULSE_COMBO, settings.shake, x, y);
    }
  }

  /** Entry into Dying is the kill edge — hp reaching 0 is a frame earlier and can be undone by the sim. */
  private syncKills(world: GameWorld, oneShots: OneShotFx, bursts: PixelBurst, settings: RendererSettings): void {
    const mobilePools = this.mobilePools;
    if (this.minimalComboPresentation && mobilePools) {
      this.syncObservedKills(world, oneShots, bursts, settings, mobilePools.enemies);
      return;
    }
    const pool = world.enemies;
    const active = pool.indices.active;
    for (let i = 0; i < this.prevEnemyState.length; i += 1) {
      if (active[i] === 0) {
        // A free slot is reused by the next spawn; a carried-over Dying would
        // swallow the new occupant's kill spark.
        this.prevEnemyState[i] = EnemyState.Free;
        continue;
      }
      const state = pool.state[i]!;
      const wasDying = this.prevEnemyState[i] === EnemyState.Dying;
      this.prevEnemyState[i] = state;
      if (state !== EnemyState.Dying || wasDying) continue;
      const x = Math.round(pool.x[i]! * WORLD_TO_R2);
      const y = Math.round(pool.y[i]! * WORLD_TO_R2);
      const big = pool.type[i]! >= BIG_ENEMY_MIN_TYPE;
      oneShots.spawn(big ? FX_SPARK_CORE : FX_SPARK_SMALL, x, y);
      bursts.burst(big ? 'kill' : 'small', x, y);
      if (!big) continue;
      this.requestImpact(IMPACT_PULSE_LARGE, settings.shake, x, y);
      this.shockRings.spawn(x, y, RING_TONE_IMPACT);
    }
  }

  private syncObservedKills(
    world: GameWorld,
    oneShots: OneShotFx,
    bursts: PixelBurst,
    settings: RendererSettings,
    observed: MobilePoolSnapshot,
  ): void {
    const pool = world.enemies;
    let activeIndex = 0;
    let deactivatedIndex = 0;
    while (activeIndex < observed.activeCount || deactivatedIndex < observed.deactivatedCount) {
      const activeSlot = activeIndex < observed.activeCount
        ? observed.activeSlots[activeIndex]!
        : POOL_CAPACITY.enemies;
      const deactivatedSlot = deactivatedIndex < observed.deactivatedCount
        ? observed.deactivatedSlots[deactivatedIndex]!
        : POOL_CAPACITY.enemies;
      if (deactivatedSlot <= activeSlot) {
        this.prevEnemyState[deactivatedSlot] = EnemyState.Free;
        deactivatedIndex += 1;
        continue;
      }

      const state = pool.state[activeSlot]!;
      const wasDying = this.prevEnemyState[activeSlot] === EnemyState.Dying;
      this.prevEnemyState[activeSlot] = state;
      activeIndex += 1;
      if (state !== EnemyState.Dying || wasDying) continue;
      const x = Math.round(pool.x[activeSlot]! * WORLD_TO_R2);
      const y = Math.round(pool.y[activeSlot]! * WORLD_TO_R2);
      const big = pool.type[activeSlot]! >= BIG_ENEMY_MIN_TYPE;
      oneShots.spawn(big ? FX_SPARK_CORE : FX_SPARK_SMALL, x, y);
      bursts.burst(big ? 'kill' : 'small', x, y);
      if (!big) continue;
      this.requestImpact(IMPACT_PULSE_LARGE, settings.shake, x, y);
      this.shockRings.spawn(x, y, RING_TONE_IMPACT);
    }
  }

  /**
   * Both ends of a player shot, read off the pool's active bits. `owner` is not
   * cleared on release, so a slot that just went free still reports who fired
   * it; a slot re-acquired in the same frame reads active and is skipped, which
   * costs one impact and never mislabels one.
   */
  private syncShots(world: GameWorld, bursts: PixelBurst): void {
    const mobilePools = this.mobilePools;
    if (this.minimalComboPresentation && mobilePools) {
      this.syncObservedShots(
        world,
        bursts,
        mobilePools.projectiles,
        mobilePools.isSceneResetFrame,
      );
      return;
    }
    const pool = world.projectiles;
    const active = pool.indices.active;
    let muzzles = 0;
    let impacts = 0;
    for (let i = 0; i < this.prevProjectileActive.length; i += 1) {
      const live = active[i]!;
      const wasLive = this.prevProjectileActive[i]!;
      this.prevProjectileActive[i] = live;
      if (live !== 0) {
        const x = pool.x[i]!;
        const y = pool.y[i]!;
        if (wasLive === 0 && pool.owner[i] === Owner.Player && muzzles < MUZZLE_PER_FRAME_CAP) {
          this.spawnMuzzle(x * WORLD_TO_R2, y * WORLD_TO_R2);
          muzzles += 1;
        }
        this.lastProjectileX[i] = x;
        this.lastProjectileY[i] = y;
        continue;
      }
      if (wasLive === 0 || pool.owner[i] !== Owner.Player || impacts >= IMPACT_BURSTS_PER_FRAME_CAP) continue;
      const x = this.lastProjectileX[i]!;
      const y = this.lastProjectileY[i]!;
      // Off-screen expiry is not an impact: the pool releases well outside the
      // frame, and debris out there would only be seen as noise at the edges.
      if (x < 0 || x > INTERNAL_WIDTH || y < 0 || y > INTERNAL_HEIGHT) continue;
      bursts.burst('small', Math.round(x * WORLD_TO_R2), Math.round(y * WORLD_TO_R2));
      impacts += 1;
    }
  }

  private syncObservedShots(
    world: GameWorld,
    bursts: PixelBurst,
    observed: MobilePoolSnapshot,
    sceneReset: boolean,
  ): void {
    const pool = world.projectiles;
    let activeIndex = 0;
    let activatedIndex = 0;
    let deactivatedIndex = sceneReset ? observed.deactivatedCount : 0;
    let muzzles = 0;
    let impacts = 0;
    while (activeIndex < observed.activeCount || deactivatedIndex < observed.deactivatedCount) {
      const activeSlot = activeIndex < observed.activeCount
        ? observed.activeSlots[activeIndex]!
        : POOL_CAPACITY.projectiles;
      const deactivatedSlot = deactivatedIndex < observed.deactivatedCount
        ? observed.deactivatedSlots[deactivatedIndex]!
        : POOL_CAPACITY.projectiles;
      if (deactivatedSlot <= activeSlot) {
        deactivatedIndex += 1;
        if (pool.owner[deactivatedSlot] !== Owner.Player || impacts >= IMPACT_BURSTS_PER_FRAME_CAP) {
          continue;
        }
        const x = this.lastProjectileX[deactivatedSlot]!;
        const y = this.lastProjectileY[deactivatedSlot]!;
        // Scene-reset deaths are skipped above. Normal off-screen expiry keeps
        // the exact same no-impact rule as the desktop full-scan path.
        if (x < 0 || x > INTERNAL_WIDTH || y < 0 || y > INTERNAL_HEIGHT) continue;
        bursts.burst('small', Math.round(x * WORLD_TO_R2), Math.round(y * WORLD_TO_R2));
        impacts += 1;
        continue;
      }

      while (
        activatedIndex < observed.activatedCount
        && observed.activatedSlots[activatedIndex]! < activeSlot
      ) {
        activatedIndex += 1;
      }
      const activated =
        activatedIndex < observed.activatedCount
        && observed.activatedSlots[activatedIndex] === activeSlot;
      if (activated) activatedIndex += 1;
      const x = pool.x[activeSlot]!;
      const y = pool.y[activeSlot]!;
      if (activated && pool.owner[activeSlot] === Owner.Player && muzzles < MUZZLE_PER_FRAME_CAP) {
        this.spawnMuzzle(x * WORLD_TO_R2, y * WORLD_TO_R2);
        muzzles += 1;
      }
      this.lastProjectileX[activeSlot] = x;
      this.lastProjectileY[activeSlot] = y;
      activeIndex += 1;
    }
  }

  private spawnMuzzle(x: number, y: number): void {
    const slot = this.muzzleCursor;
    this.muzzleCursor = (slot + 1) % MUZZLE_SLOTS;
    this.muzzleX[slot] = x;
    this.muzzleY[slot] = y;
    this.muzzleAgeMs[slot] = 0;
    this.muzzleGrade[slot] = 0;
    this.muzzleLive[slot] = 1;
  }

  /**
   * Age every slot into a grade, then draw one pass per grade: the rects of a
   * grade share a colour and an alpha, so the whole system costs as many
   * `fill()` calls as there are grades regardless of how many slots are live.
   */
  private drawMuzzle(muzzle: Graphics, deltaMs: number): void {
    for (let i = 0; i < MUZZLE_SLOTS; i += 1) {
      if (this.muzzleLive[i] === 0) continue;
      const age = this.muzzleAgeMs[i]! + deltaMs;
      this.muzzleAgeMs[i] = age;
      if (age >= MUZZLE_LIFE_MS) {
        this.muzzleLive[i] = 0;
        continue;
      }
      this.muzzleGrade[i] = Math.min(MUZZLE_GRADES.length - 1, Math.floor((age / MUZZLE_LIFE_MS) * MUZZLE_GRADES.length));
    }
    if (this.minimalComboPresentation) {
      for (let i = 0; i < MUZZLE_SLOTS; i += 1) {
        const live = this.muzzleLive[i] !== 0;
        const grade = MUZZLE_GRADES[this.muzzleGrade[i]!] ?? MUZZLE_GRADES[0]!;
        const x = Math.round(this.muzzleX[i]!);
        const y = Math.round(this.muzzleY[i]!);
        for (let p = 0; p < MUZZLE_OFFSETS_X.length; p += 1) {
          const particle = this.muzzleParticles[i * MUZZLE_OFFSETS_X.length + p]!;
          particle.alpha = live ? grade.style.alpha : 0;
          if (!live) continue;
          particle.tint = grade.style.color;
          particle.scaleX = grade.size / WHITE_WIDTH;
          particle.scaleY = grade.size / WHITE_HEIGHT;
          particle.x = x + MUZZLE_OFFSETS_X[p]!;
          particle.y = y + MUZZLE_OFFSETS_Y[p]!;
        }
      }
      return;
    }
    for (let g = 0; g < MUZZLE_GRADES.length; g += 1) {
      const grade = MUZZLE_GRADES[g]!;
      let drawn = 0;
      for (let i = 0; i < MUZZLE_SLOTS; i += 1) {
        if (this.muzzleLive[i] === 0 || this.muzzleGrade[i] !== g) continue;
        const x = Math.round(this.muzzleX[i]!);
        const y = Math.round(this.muzzleY[i]!);
        for (let p = 0; p < MUZZLE_OFFSETS_X.length; p += 1) {
          muzzle.rect(x + MUZZLE_OFFSETS_X[p]!, y + MUZZLE_OFFSETS_Y[p]!, grade.size, grade.size);
        }
        drawn += 1;
      }
      if (drawn > 0) muzzle.fill(grade.style);
    }
  }

  /**
   * The boost is a multiplier on how far the *background* has moved this frame,
   * accumulated rather than applied to scrollX directly: the sim's scroll stays
   * the truth, and dropping a tier leaves the extra distance already travelled
   * in place instead of snapping the parallax backwards. A base boost above 1
   * needs nothing special from the accumulator — it simply never stops adding.
   */
  private syncSpeedLayer(world: GameWorld, deltaMs: number): void {
    const seconds = deltaMs / MS_PER_SECOND;
    const tier = this.minimalComboPresentation ? 0 : comboTierIndex(world.player.chain);
    const target = SCROLL_BOOST_BY_TIER[tier]!;
    const approach = 1 - Math.exp(-seconds / BOOST_TIME_CONSTANT_S);
    this.boost += (target - this.boost) * approach;
    const advanced = world.scrollX - this.prevScrollX;
    this.prevScrollX = world.scrollX;
    if (advanced > 0) this.extraScroll += (this.boost - 1) * advanced;
    this.advanceSpeedLines(seconds, SPEED_LINES_BY_TIER[tier]!);
  }

  private advanceSpeedLines(seconds: number, wanted: number): void {
    let liveCount = 0;
    for (let i = 0; i < SPEED_LINE_SLOTS; i += 1) {
      if (this.lineLive[i] === 0) continue;
      const x = this.lineX[i]! - this.lineSpeed[i]! * this.boost * seconds;
      this.lineX[i] = x;
      if (x + this.lineLength[i]! < 0) {
        this.lineLive[i] = 0;
        continue;
      }
      liveCount += 1;
    }
    // Density ramps in over a few frames so a tier crossing reads as building speed, not as a curtain.
    let spawned = 0;
    while (liveCount < wanted && spawned < SPEED_LINE_SPAWNS_PER_FRAME) {
      if (!this.spawnSpeedLine()) break;
      liveCount += 1;
      spawned += 1;
    }
  }

  private spawnSpeedLine(): boolean {
    for (let attempt = 0; attempt < SPEED_LINE_SLOTS; attempt += 1) {
      const slot = this.lineCursor;
      this.lineCursor = (slot + 1) % SPEED_LINE_SLOTS;
      if (this.lineLive[slot] !== 0) continue;
      this.lineX[slot] = R2_WIDTH + Math.random() * SPEED_LINE_SPAWN_MARGIN_PX;
      this.lineY[slot] = Math.floor(Math.random() * R2_HEIGHT);
      this.lineLength[slot] = SPEED_LINE_MIN_LENGTH_PX + Math.random() * (SPEED_LINE_MAX_LENGTH_PX - SPEED_LINE_MIN_LENGTH_PX);
      this.lineSpeed[slot] =
        SPEED_LINE_MIN_SPEED_PX_PER_SECOND + Math.random() * (SPEED_LINE_MAX_SPEED_PX_PER_SECOND - SPEED_LINE_MIN_SPEED_PX_PER_SECOND);
      this.lineGrade[slot] = slot % SPEED_LINE_GRADES.length;
      this.lineLive[slot] = 1;
      return true;
    }
    return false;
  }

  private drawSpeedLines(speedLines: Graphics): void {
    if (this.minimalComboPresentation) {
      for (let i = 0; i < SPEED_LINE_SLOTS; i += 1) {
        const particle = this.speedParticles[i]!;
        if (this.lineLive[i] === 0) {
          particle.alpha = 0;
          continue;
        }
        const grade = SPEED_LINE_GRADES[this.lineGrade[i]!]!;
        particle.tint = grade.style.color;
        particle.alpha = grade.style.alpha;
        particle.scaleX = Math.round(this.lineLength[i]!) / WHITE_WIDTH;
        particle.scaleY = grade.height / WHITE_HEIGHT;
        particle.x = Math.round(this.lineX[i]!);
        particle.y = this.lineY[i]!;
      }
      return;
    }
    for (let g = 0; g < SPEED_LINE_GRADES.length; g += 1) {
      const grade = SPEED_LINE_GRADES[g]!;
      let drawn = 0;
      for (let i = 0; i < SPEED_LINE_SLOTS; i += 1) {
        if (this.lineLive[i] === 0 || this.lineGrade[i] !== g) continue;
        speedLines.rect(Math.round(this.lineX[i]!), this.lineY[i]!, Math.round(this.lineLength[i]!), grade.height);
        drawn += 1;
      }
      if (drawn > 0) speedLines.fill(grade.style);
    }
  }

  /**
   * Exhaust ages by wall time, not by frame, so the trail keeps its length
   * through a hitch; the spawn credit is likewise a time budget, capped per
   * frame so a long frame cannot dump the whole pool at one x.
   */
  private syncExhaust(world: GameWorld, deltaMs: number): void {
    const seconds = deltaMs / MS_PER_SECOND;
    for (let i = 0; i < EXHAUST_SLOTS; i += 1) {
      if (this.exhaustLive[i] === 0) continue;
      const age = this.exhaustAgeMs[i]! + deltaMs;
      const life = this.exhaustLifeMs[i]!;
      if (age >= life) {
        this.exhaustLive[i] = 0;
        continue;
      }
      this.exhaustAgeMs[i] = age;
      this.exhaustX[i] = this.exhaustX[i]! - this.exhaustDrift[i]! * seconds;
      this.exhaustGrade[i] = Math.min(EXHAUST_GRADES.length - 1, Math.floor((age / life) * EXHAUST_GRADES.length));
    }
    const player = world.player;
    if (!player.alive) {
      this.exhaustCredit = 0;
      return;
    }
    const tier = this.minimalComboPresentation ? 0 : comboTierIndex(player.chain);
    this.exhaustCredit += seconds * EXHAUST_SPAWNS_PER_SECOND * (1 + EXHAUST_SPAWN_RATE_PER_TIER * tier);
    const x = player.x * WORLD_TO_R2 - EXHAUST_REAR_OFFSET_PX;
    const y = player.y * WORLD_TO_R2;
    let spawned = 0;
    while (this.exhaustCredit >= 1 && spawned < EXHAUST_SPAWNS_PER_FRAME_CAP) {
      this.exhaustCredit -= 1;
      this.spawnExhaust(x, y);
      spawned += 1;
    }
    if (this.exhaustCredit > EXHAUST_SPAWNS_PER_FRAME_CAP) this.exhaustCredit = EXHAUST_SPAWNS_PER_FRAME_CAP;
  }

  /** Ring cursor with a hard cap: the oldest particle is overwritten rather than skipped, so the stream never gaps. */
  private spawnExhaust(x: number, y: number): void {
    const slot = this.exhaustCursor;
    this.exhaustCursor = (slot + 1) % EXHAUST_SLOTS;
    const jitter = Math.round((Math.random() * 2 - 1) * EXHAUST_JITTER_Y_PX);
    this.exhaustX[slot] = x;
    this.exhaustY[slot] = y + jitter;
    this.exhaustDrift[slot] = EXHAUST_DRIFT_MIN_PX_PER_SECOND + Math.random() * (EXHAUST_DRIFT_MAX_PX_PER_SECOND - EXHAUST_DRIFT_MIN_PX_PER_SECOND);
    this.exhaustAgeMs[slot] = 0;
    this.exhaustLifeMs[slot] = EXHAUST_LIFE_MIN_MS + Math.random() * (EXHAUST_LIFE_MAX_MS - EXHAUST_LIFE_MIN_MS);
    this.exhaustGrade[slot] = 0;
    this.exhaustLive[slot] = 1;
  }

  private drawExhaust(exhaust: Graphics): void {
    if (this.minimalComboPresentation) {
      for (let i = 0; i < EXHAUST_SLOTS; i += 1) {
        const particle = this.exhaustParticles[i]!;
        if (this.exhaustLive[i] === 0) {
          particle.alpha = 0;
          continue;
        }
        const grade = EXHAUST_GRADES[this.exhaustGrade[i]!]!;
        particle.tint = grade.style.color;
        particle.alpha = grade.style.alpha;
        particle.scaleX = grade.size / WHITE_WIDTH;
        particle.scaleY = grade.size / WHITE_HEIGHT;
        particle.x = Math.round(this.exhaustX[i]!);
        particle.y = Math.round(this.exhaustY[i]!);
      }
      return;
    }
    for (let g = 0; g < EXHAUST_GRADES.length; g += 1) {
      const grade = EXHAUST_GRADES[g]!;
      let drawn = 0;
      for (let i = 0; i < EXHAUST_SLOTS; i += 1) {
        if (this.exhaustLive[i] === 0 || this.exhaustGrade[i] !== g) continue;
        exhaust.rect(Math.round(this.exhaustX[i]!), Math.round(this.exhaustY[i]!), grade.size, grade.size);
        drawn += 1;
      }
      if (drawn > 0) exhaust.fill(grade.style);
    }
  }

  private requestImpact(amount: number, intensity: number, x: number, y: number): void {
    if (intensity <= 0) return;
    const next = Math.min(1, amount * intensity);
    if (next < this.impactPulse) return;
    this.impactPulse = next;
    this.impactX = x;
    this.impactY = y;
  }

  /**
   * 四辺から衝撃点へ伸びる光路。画面内の物体は一切動かさず、線の到達距離だけで
   * 圧縮を示す。リングの放射と逆方向の動きなので、静止画でも衝撃点が一読できる。
   */
  private drawImpactCompression(rings: Graphics, settings: RendererSettings): void {
    const pulse = this.impactPulse;
    if (pulse <= 0) return;
    const reduction = settings.flashReduction ? 0.42 : 1;
    const alpha = pulse * 0.72 * reduction;
    const x = Math.max(0, Math.min(R2_WIDTH, Math.round(this.impactX)));
    const y = Math.max(0, Math.min(R2_HEIGHT, Math.round(this.impactY)));
    const gap = Math.round(16 + (1 - pulse) * 74);
    const leftWidth = Math.max(0, x - gap);
    const rightX = Math.min(R2_WIDTH, x + gap);
    const topHeight = Math.max(0, y - gap);
    const bottomY = Math.min(R2_HEIGHT, y + gap);
    if (leftWidth > 0) rings.rect(0, y - 1, leftWidth, 3);
    if (rightX < R2_WIDTH) rings.rect(rightX, y - 1, R2_WIDTH - rightX, 3);
    if (topHeight > 0) rings.rect(x - 1, 0, 3, topHeight);
    if (bottomY < R2_HEIGHT) rings.rect(x - 1, bottomY, 3, R2_HEIGHT - bottomY);
    rings.fill({ color: 0xd9fbff, alpha });
    const inset = Math.round((1 - pulse) * 34);
    rings.rect(inset, inset, R2_WIDTH - inset * 2, R2_HEIGHT - inset * 2);
    rings.stroke({ color: 0x83f5ff, alpha: alpha * 0.58, width: pulse > 0.66 ? 3 : 2 });
  }

  private drawMobileImpactCompression(
    rings: MobileImpactPrimitives,
    settings: RendererSettings,
  ): void {
    const pulse = this.impactPulse;
    if (pulse <= 0) return;
    const reduction = settings.flashReduction ? 0.42 : 1;
    const alpha = pulse * 0.72 * reduction;
    const x = Math.max(0, Math.min(R2_WIDTH, Math.round(this.impactX)));
    const y = Math.max(0, Math.min(R2_HEIGHT, Math.round(this.impactY)));
    const gap = Math.round(16 + (1 - pulse) * 74);
    const inset = Math.round((1 - pulse) * 34);
    rings.setImpactCompression(x, y, gap, inset, alpha, pulse > 0.66 ? 3 : 2);
  }

  /** カメラは常に等倍固定。色収差だけを同じ衝撃エンベロープで駆動する。 */
  private applyImpactPulse(deltaMs: number, settings: RendererSettings): void {
    const host = this.host;
    if (!host) return;
    host.setImpactZoom(1);
    host.setImpactPulse(settings.flashReduction ? 0 : this.impactPulse);
    this.impactPulse *= Math.exp(-deltaMs / IMPACT_PULSE_TIME_CONSTANT_MS);
    if (this.impactPulse < IMPACT_PULSE_EPSILON) this.impactPulse = 0;
  }

  /**
   * Mobile keeps the exact pixel vocabulary but uploads three fixed quad
   * buffers. No Graphics.clear()/path rebuild or battle-time scene mutation is
   * left on the speed, exhaust or muzzle paths.
   */
  private initMobileParticles(speedLayer: Container, exhaustLayer: Container, muzzleLayer: Container): void {
    for (let i = 0; i < SPEED_LINE_SLOTS; i += 1) {
      this.speedParticles.push(new Particle({ texture: Texture.WHITE, alpha: 0 }));
    }
    for (let i = 0; i < EXHAUST_SLOTS; i += 1) {
      this.exhaustParticles.push(new Particle({ texture: Texture.WHITE, alpha: 0 }));
    }
    for (let i = 0; i < MUZZLE_SLOTS * MUZZLE_OFFSETS_X.length; i += 1) {
      this.muzzleParticles.push(new Particle({ texture: Texture.WHITE, alpha: 0 }));
    }
    speedLayer.addChild(this.mobileContainer(this.speedParticles));
    exhaustLayer.addChild(this.mobileContainer(this.exhaustParticles));
    muzzleLayer.addChild(this.mobileContainer(this.muzzleParticles));
  }

  private mobileContainer(particles: Particle[]): ParticleContainer {
    return new ParticleContainer({
      texture: Texture.WHITE,
      particles,
      boundsArea: new Rectangle(0, 0, R2_WIDTH, R2_HEIGHT),
      roundPixels: true,
      dynamicProperties: {
        position: true,
        vertex: true,
        rotation: false,
        uvs: false,
        color: true,
      },
    });
  }
}

/** Mirrors comboPhase.comboTierIndex over the duplicated thresholds above. */
function comboTierIndex(chain: number): number {
  for (let i = COMBO_TIER_CHAINS.length - 1; i > 0; i -= 1) {
    if (chain >= COMBO_TIER_CHAINS[i]!) return i;
  }
  return 0;
}
